import {
  AutomaticSavingsFeaturesResultTypenames,
  Mutation,
  MutationUpdateSycArgs,
  Query,
  UpdateSycError,
  UpdateSYCResultTypenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { UpdateSYC } from 'services/graphql/mutations/UpdateSYC';
import GetAutomaticSavings from 'services/graphql/queries/GetAutomaticSavings';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const AutomaticSavings = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'automaticSavings',
  });
  const { defaultMessage, defaultTitle } = useDefaultErrorStrings();
  const snackbarDuration = 2750;
  const [snackbar, setSnackbar] = useState<{ message: string }>({
    message: '',
  });
  const [updateSYCResponseError, setUpdateSYCResponseError] =
    useState<UpdateSycError | null>(null);
  const [missingUpdateSYCResponseError, setMissingUpdateSYCResponseError] =
    useState<Error | null>(null);
  const [pendingSYCStatus, setPendingSYCStatus] = useState<boolean | null>(
    null,
  );
  const [isSYCEnabledOverride, setIsSYCEnabledOverride] = useState<
    boolean | null
  >(null);
  const [{ data, fetching, error }, refetchBaseQuery] = useQuery<
    Pick<Query, 'user'>
  >({
    query: GetAutomaticSavings,
    requestPolicy: 'network-only',
  });
  const automaticSavingsResult = data?.user?.customerInfo?.automaticSavings;
  const automaticSavings =
    automaticSavingsResult?.__typename ===
    AutomaticSavingsFeaturesResultTypenames.AutomaticSavingsFeaturesEnabled
      ? automaticSavingsResult
      : undefined;
  const sypPercentage = automaticSavings?.syp.percentage;
  const roundedSYPPercentage =
    sypPercentage === undefined
      ? 0
      : Math.round(Number.parseFloat(sypPercentage.toString())) || 0;
  const serverIsSYCEnabled = automaticSavings?.syc.enabled ?? false;
  const isSYCEnabled = isSYCEnabledOverride ?? serverIsSYCEnabled;

  const [{ fetching: updateSYCFetching, error: updateSYCError }, updateSYC] =
    useCancellableMutation<Pick<Mutation, 'updateSYC'>, MutationUpdateSycArgs>(
      UpdateSYC,
    );
  const updateSYCFailure =
    updateSYCError ?? updateSYCResponseError ?? missingUpdateSYCResponseError;
  const screenError = error ?? updateSYCFailure;

  useViewDidFocusScreenAnalyticsEvent({
    key: 'Automatic Savings Manage',
  });

  useAutoHideSnackbar(
    Boolean(snackbar.message),
    () => {
      setSnackbar({ message: '' });
    },
    snackbarDuration,
  );

  useFocusEffect(
    useCallback(() => {
      refetchBaseQuery();
    }, [refetchBaseQuery]),
  );

  const onUpdateSYC = async (updatedSYCStatus: boolean) => {
    setPendingSYCStatus(updatedSYCStatus);
    setUpdateSYCResponseError(null);
    setMissingUpdateSYCResponseError(null);

    try {
      const response = await updateSYC({
        input: {
          enabled: updatedSYCStatus,
        },
      });

      if (response.error) {
        return;
      }

      const updateSYCResult = response.data?.updateSYC;
      if (
        updateSYCResult?.__typename === UpdateSYCResultTypenames.UpdateSYCError
      ) {
        setUpdateSYCResponseError(updateSYCResult);
        return;
      }

      if (
        updateSYCResult?.__typename !==
        UpdateSYCResultTypenames.UpdateSYCSuccess
      ) {
        setMissingUpdateSYCResponseError(new Error(defaultMessage));
        return;
      }

      setIsSYCEnabledOverride(updateSYCResult.syc.enabled);
      setPendingSYCStatus(null);
      refetchBaseQuery();
      if (
        'successMessage' in updateSYCResult &&
        typeof updateSYCResult.successMessage === 'string' &&
        updateSYCResult.successMessage
      ) {
        setSnackbar({
          message: updateSYCResult.successMessage,
        });
      }
    } catch (mutationError) {
      if (mutationError instanceof AbortError) {
        return;
      }

      throw mutationError;
    }
  };

  const handleTryAgain = () => {
    if (error || pendingSYCStatus === null) {
      refetchBaseQuery();
      return;
    }

    void onUpdateSYC(pendingSYCStatus);
  };

  const onPressSYP = () => {
    router.push({
      pathname: '/accounts/save-your-pay',
    });
  };

  return (
    <View style={styles.screenContainer}>
      <Stack.Screen
        options={{
          title: translatedText('headerTitle'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {snackbar.message ? (
        <Snackbar duration={snackbarDuration} text={snackbar.message} />
      ) : null}

      <ListItem
        primaryCopy={translatedText('sycPrimaryText')}
        secondaryCopy={translatedText('sycSecondaryText')}
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.SWITCH,
          props: {
            isOn: isSYCEnabled,
            onValueChange: (updatedSYCStatus) => {
              void onUpdateSYC(updatedSYCStatus);
            },
            analyticsId: isSYCEnabled
              ? 'saveYourChangesTurnedOff'
              : 'saveYourChangesTurnedOn',
          },
        }}
        showsChevron={false}
        secondaryNumberOfLines={2}
      />
      <View style={styles.dividerWrapper}>
        <HairLineDivider />
      </View>
      <ListItem
        onPress={onPressSYP}
        primaryCopy={translatedText('sypPrimaryText')}
        accessibilityLabel="syp pressable"
        secondaryCopy={translatedText('sypSecondaryText')}
        trailingAccessory={{
          id: '2',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: `${roundedSYPPercentage}%`,
          },
        }}
        secondaryNumberOfLines={2}
        analyticsId="saveYourPayClicked"
      />

      {/* For managing loading and error states */}
      <ScreenTransitionOverlay
        isLoading={fetching || updateSYCFetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          screenError ? (
            <DefaultPageDownErrorMessage
              error={screenError}
              title={updateSYCResponseError?.title ?? defaultTitle}
              message={screenError.message || defaultMessage}
              tryAgainAction={handleTryAgain}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default AutomaticSavings;
